import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../../../StockControl';
import { ARGENTINA_PROVINCES } from '../../logistics/components/LogisticsUtils';
import {
  buildOutboundOrderHeaderSummary,
  computeOutboundOrderLineStatus,
} from '../stockFlowUtils';
import { stockButtonTones } from '../stockUi';

const OUTBOUND_STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  packing: 'bg-amber-100 text-amber-700',
  ready_for_delivery: 'bg-emerald-100 text-emerald-700',
  partially_assigned: 'bg-violet-100 text-violet-700',
  assigned_to_route: 'bg-cyan-100 text-cyan-700',
  in_transit: 'bg-sky-100 text-sky-700',
  in_warranty: 'bg-fuchsia-100 text-fuchsia-700 animate-pulse',
  ready_for_pickup: 'bg-indigo-100 text-indigo-700',
  partially_delivered: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-200 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-700',
};

const OUTBOUND_STATUS_LABELS = {
  draft: 'Borrador',
  packing: 'Preparando',
  ready_for_delivery: 'Listo para entregar',
  partially_assigned: 'Parcialmente asignado',
  assigned_to_route: 'Asignado a ruta',
  in_transit: 'En tránsito',
  in_warranty: 'En garantía',
  ready_for_pickup: 'Listo para retiro',
  partially_delivered: 'Entrega parcial',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const LINE_STATUS_LABELS = {
  draft: 'Borrador',
  ready_for_delivery: 'Listo',
  partially_assigned: 'Parcial',
  assigned_to_route: 'Asignado',
  in_transit: 'En tránsito',
  in_warranty: 'En garantía',
  ready_for_pickup: 'Listo retiro',
  partially_delivered: 'Parcial',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const OPERATION_TYPE_OPTIONS = [
  { value: 'sale', label: 'Venta' },
  { value: 'warranty', label: 'Garantía' },
  { value: 'replacement', label: 'Reposición' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'transfer', label: 'Traslado' },
];

const ORDER_READY_STATES = [
  { value: 'packing', label: 'Preparando pedido' },
  { value: 'ready_for_delivery', label: 'Listo para entregar' },
];
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

const ACTIVE_ORDER_STATUSES = new Set([
  'draft',
  'packing',
  'ready_for_delivery',
  'partially_assigned',
  'assigned_to_route',
  'in_transit',
  'in_warranty',
  'ready_for_pickup',
  'partially_delivered',
]);

const getDisplayOrderStatus = (order = {}) => {
  const explicit = String(order?.status || '').trim();
  if (['in_warranty', 'ready_for_pickup'].includes(explicit)) return explicit;
  return buildOutboundOrderHeaderSummary(order, order.lines || []).status;
};

const WARRANTY_OUTCOME_LABELS = {
  fixed: 'Arreglada',
  not_repaired: 'No reparada',
  replaced_new: 'Cambio por nuevo',
};

const formatMoney = (value, currency = 'ARS') => {
  const numeric = Number(value || 0);
  if (!numeric) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(numeric);
  } catch {
    return `${currency} ${numeric.toLocaleString('es-AR')}`;
  }
};

const formatAddress = (values = {}) =>
  [
    values.address,
    values.apartment,
    values.locality,
    values.partido,
    values.province,
    values.postalCode,
  ]
    .filter(Boolean)
    .join(', ');

const normalizeAddressValidationState = ({
  status = GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
  text,
  validatedAddress = ''
} = {}) => ({
  status,
  text:
    text ||
    (GEOAPIFY_API_KEY
      ? 'Escribí la dirección y elegí una sugerencia o validala para dejar el destino listo.'
      : 'Completá la dirección y validala manualmente para dejar el destino listo.'),
  validatedAddress,
});

const buildDestinationAutocompleteSearchText = (values = {}) =>
  [
    values.destinationAddress,
    values.destinationLocality,
    values.destinationPartido,
    values.destinationProvince,
    'Argentina',
  ]
    .filter(Boolean)
    .join(', ');

const buildDestinationValidatedAddressText = (values = {}) =>
  [
    values.destinationAddress,
    values.destinationApartment,
    values.destinationLocality,
    values.destinationPartido,
    values.destinationProvince,
    values.destinationPostalCode,
    'Argentina',
  ]
    .filter(Boolean)
    .join(', ');

const mapGeoapifySuggestionToDestinationPatch = (feature = {}) => {
  const props = feature?.properties || {};
  return {
    destinationAddress:
      [props.street, props.housenumber].filter(Boolean).join(' ').trim() ||
      props.formatted ||
      '',
    destinationApartment: '',
    destinationLocality:
      props.city || props.town || props.village || props.suburb || props.county || '',
    destinationPartido: props.county || props.state_district || '',
    destinationProvince: props.state || 'CABA',
    destinationPostalCode: props.postcode || '',
    destinationLatlng: feature?.geometry?.coordinates
      ? { lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] }
      : null,
  };
};

const toSafeNumber = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getDateMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatDaysSince = (value) => {
  const dateMs = getDateMs(value);
  if (!dateMs) return '';
  const diffDays = Math.max(0, Math.floor((Date.now() - dateMs) / 86400000));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return '1 día';
  return `${diffDays} días`;
};

const getProductPrimaryLabel = (product = {}) => {
  const category = String(product?.category || '').trim();
  if (category) return category;
  const name = String(product?.name || '').trim();
  if (!name) return 'Producto';
  return name.split(/\s+/)[0] || 'Producto';
};

const getProductIdentityParts = (product = {}) => {
  const parts = [];
  const pushUnique = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized || parts.includes(normalized)) return;
    parts.push(normalized);
  };

  pushUnique(product?.model);
  pushUnique(product?.brand);
  pushUnique(product?.featureLabel);
  if (product?.inches) pushUnique(`${product.inches}"`);
  if (!parts.length && product?.sku) pushUnique(product.sku);
  return parts;
};

const buildProductOptionLabel = (product = {}) => {
  const primary = getProductPrimaryLabel(product);
  const identity = getProductIdentityParts(product);
  const readyQty = getProductReadyStockQty(product);
  const pieces = [primary, ...identity];
  const baseLabel = pieces.filter(Boolean).join(' · ');
  return `${baseLabel || product?.name || 'Producto'} · Stock ${readyQty}`;
};

const getProductReadyStockQty = (product) => {
  if (!product) return 0;
  const physicalQty = toSafeNumber(product?.stockBuckets?.physicalQty);
  const pendingOutboundQty = toSafeNumber(product?.stockBuckets?.pendingOutboundQty);
  return Math.max(0, physicalQty - pendingOutboundQty);
};

const StockOutboundOrdersPanel = ({
  orders,
  products,
  purchaseLines = [],
  contacts,
  form,
  setForm,
  editingOrderId,
  onCancelEdit,
  onEditOrder,
  onDeleteOrder,
  onApproveWarrantyPickup,
  onDestinationSelect,
  onDestinationAddressPatch,
  onGeocodeDestination,
  onCreate,
}) => {
  const productOptions = useMemo(
    () =>
      (products || [])
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')),
    [products]
  );

  const contactOptions = useMemo(
    () =>
      (contacts || [])
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')),
    [contacts]
  );
  const selectedContact = useMemo(
    () => contactOptions.find((contact) => contact.id === form.selectedContactId) || null,
    [contactOptions, form.selectedContactId]
  );
  const selectedContactAddress = useMemo(
    () => (selectedContact?.addresses && selectedContact.addresses[0]) || null,
    [selectedContact]
  );
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressValidation, setAddressValidation] = useState(() =>
    normalizeAddressValidationState()
  );
  const addressAutocompleteRef = useRef(null);

  const latestPurchaseLineByProductId = useMemo(() => {
    const grouped = {};
    (purchaseLines || []).forEach((line) => {
      const productId = String(line?.productId || '');
      if (!productId) return;
      const currentTime = Math.max(
        toSafeNumber(line?.updatedAt?.seconds) * 1000 || 0,
        toSafeNumber(line?.createdAt?.seconds) * 1000 || 0,
        line?.updatedAt instanceof Date ? line.updatedAt.getTime() : 0,
        line?.createdAt instanceof Date ? line.createdAt.getTime() : 0,
        Number.isNaN(new Date(line?.updatedAt).getTime()) ? 0 : new Date(line?.updatedAt).getTime(),
        Number.isNaN(new Date(line?.createdAt).getTime()) ? 0 : new Date(line?.createdAt).getTime()
      );
      const previous = grouped[productId];
      const previousTime = previous?._sortTime || 0;
      if (!previous || currentTime >= previousTime) {
        grouped[productId] = {
          ...line,
          _sortTime: currentTime,
        };
      }
    });
    return grouped;
  }, [purchaseLines]);

  useEffect(() => {
    if (selectedContact) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      setAddressValidation(
        normalizeAddressValidationState({
          status: selectedContactAddress?.latlng ? 'success' : 'idle',
          text: selectedContactAddress?.latlng
            ? 'Destino existente con ubicación ya validada.'
            : 'Destino existente sin coordenadas guardadas. Podés completarlas desde contactos o validarlo antes de guardar.',
          validatedAddress: formatAddress(selectedContactAddress || {}),
        })
      );
      return;
    }

    setAddressValidation((prev) => {
      if (!form.destinationLatlng) {
        return normalizeAddressValidationState({
          status: GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
          validatedAddress: '',
        });
      }
      return normalizeAddressValidationState({
        status: 'success',
        text: 'Destino validado para este pedido de salida.',
        validatedAddress: buildDestinationValidatedAddressText({
          destinationAddress: form.destinationAddress,
          destinationApartment: form.destinationApartment,
          destinationLocality: form.destinationLocality,
          destinationPartido: form.destinationPartido,
          destinationProvince: form.destinationProvince,
          destinationPostalCode: form.destinationPostalCode,
        }),
      });
    });
  }, [
    selectedContact,
    selectedContactAddress,
    form.destinationLatlng,
    form.destinationAddress,
    form.destinationApartment,
    form.destinationLocality,
    form.destinationPartido,
    form.destinationProvince,
    form.destinationPostalCode,
  ]);

  useEffect(() => {
    if (selectedContact || !GEOAPIFY_API_KEY) return undefined;

    const queryText = String(form.destinationAddress || '').trim();
    const shouldQuery =
      queryText.length >= 5 &&
      (String(form.destinationLocality || '').trim() ||
        String(form.destinationPartido || '').trim() ||
        /\d/.test(queryText));

    if (!shouldQuery) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setLoadingAddressSuggestions(true);
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
            buildDestinationAutocompleteSearchText(form)
          )}` +
          `&lang=es&limit=5&filter=countrycode:ar&apiKey=${GEOAPIFY_API_KEY}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Geoapify autocomplete error: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data?.features) ? data.features : [];
        setAddressSuggestions(results);
        setShowAddressSuggestions(true);
        setAddressValidation((prev) =>
          prev.status === 'success'
            ? prev
            : normalizeAddressValidationState({
                status: results.length ? 'idle' : 'error',
                text: results.length
                  ? 'Elegí una sugerencia o validá manualmente la dirección del destino.'
                  : 'No encontré coincidencias claras. Completá mejor la dirección o validá manualmente.',
                validatedAddress: prev.validatedAddress,
              })
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
          setAddressValidation((prev) =>
            prev.status === 'success'
              ? prev
              : normalizeAddressValidationState({
                  status: 'error',
                  text: 'No pude cargar sugerencias de dirección. Probá validar manualmente.',
                  validatedAddress: prev.validatedAddress,
                })
          );
        }
      } finally {
        setLoadingAddressSuggestions(false);
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [
    selectedContact,
    form.destinationAddress,
    form.destinationLocality,
    form.destinationPartido,
    form.destinationProvince,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!addressAutocompleteRef.current?.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAddressSuggestion = useCallback(
    (feature) => {
      const patch = mapGeoapifySuggestionToDestinationPatch(feature);
      onDestinationAddressPatch(patch);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'success',
          text: 'Dirección sugerida seleccionada y aprobada para este destino.',
          validatedAddress:
            feature?.properties?.formatted || buildDestinationValidatedAddressText(patch),
        })
      );
    },
    [onDestinationAddressPatch]
  );

  const handleValidateDestinationAddress = useCallback(async () => {
    const coords = await onGeocodeDestination();
    if (coords) {
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'success',
          text: 'Destino validado y listo para pasar a logística.',
          validatedAddress: buildDestinationValidatedAddressText({
            destinationAddress: form.destinationAddress,
            destinationApartment: form.destinationApartment,
            destinationLocality: form.destinationLocality,
            destinationPartido: form.destinationPartido,
            destinationProvince: form.destinationProvince,
            destinationPostalCode: form.destinationPostalCode,
          }),
        })
      );
      setShowAddressSuggestions(false);
    } else {
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'error',
          text: 'No pude validar la ubicación. Revisá la dirección o elegí una sugerencia.',
          validatedAddress: '',
        })
      );
    }
  }, [
    onGeocodeDestination,
    form.destinationAddress,
    form.destinationApartment,
    form.destinationLocality,
    form.destinationPartido,
    form.destinationProvince,
    form.destinationPostalCode,
  ]);

  const activeOrders = useMemo(
    () =>
      (orders || []).filter((order) => {
        return ACTIVE_ORDER_STATUSES.has(getDisplayOrderStatus(order));
      }),
    [orders]
  );

  const linesWithProducts = useMemo(
    () =>
      (form.lines || []).map((line) => {
        const product = productOptions.find((item) => item.id === line.productId) || null;
        const availableQty = getProductReadyStockQty(product);
        const quantity = Math.max(0, Math.min(toSafeNumber(line.quantity), availableQty || 0));
        const latestPurchaseLine = latestPurchaseLineByProductId[String(line.productId || '')] || null;
        const unitCost = latestPurchaseLine
          ? toSafeNumber(latestPurchaseLine.unitCost)
          : product
            ? toSafeNumber(product.defaultPurchaseCost)
            : 0;
        const saleUnitPrice =
          line.saleUnitPrice !== '' && line.saleUnitPrice != null
            ? toSafeNumber(line.saleUnitPrice)
            : product
            ? toSafeNumber(product.defaultSalePrice)
            : 0;
        return {
          ...line,
          product,
          latestPurchaseLine,
          availableQty,
          quantity,
          unitCost,
          saleUnitPrice,
          totalCost: quantity * unitCost,
          totalSale: quantity * saleUnitPrice,
          margin: quantity * saleUnitPrice - quantity * unitCost,
        };
      }),
    [form.lines, latestPurchaseLineByProductId, productOptions]
  );

  const totals = useMemo(
    () =>
      linesWithProducts.reduce(
        (acc, line) => ({
          quantity: acc.quantity + line.quantity,
          totalCost: acc.totalCost + line.totalCost,
          totalSale: acc.totalSale + line.totalSale,
          margin: acc.margin + line.margin,
        }),
        { quantity: 0, totalCost: 0, totalSale: 0, margin: 0 }
      ),
    [linesWithProducts]
  );

  const updateLine = (lineId, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
    }));
  };

  const removeLine = (lineId) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.length > 1 ? prev.lines.filter((line) => line.id !== lineId) : prev.lines,
    }));
  };

  const addLine = () => {
    setForm((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          productId: '',
          quantity: '',
          saleUnitPrice: '',
        },
      ],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_22px_50px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">
              Salidas preparadas
            </p>
            <h3 className="mt-1 text-2xl font-bold text-slate-950">Pedidos de salida</h3>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Acá preparás lo que sale: definís productos, cantidades, destino y referencia comercial.
              Cuando queda listo, logística lo toma como pendiente de entrega.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Pedidos</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{activeOrders.length}</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Listos</p>
              <p className="mt-1 text-xl font-bold text-emerald-800">
                {activeOrders.filter((item) => buildOutboundOrderHeaderSummary(item, item.lines || []).status === 'ready_for_delivery').length}
              </p>
            </div>
            <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-600">Asignados</p>
              <p className="mt-1 text-xl font-bold text-violet-800">
                {activeOrders.filter((item) => {
                  const status = buildOutboundOrderHeaderSummary(item, item.lines || []).status;
                  return status === 'assigned_to_route' || status === 'partially_assigned';
                }).length}
              </p>
            </div>
            <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-600">Entregados</p>
              <p className="mt-1 text-xl font-bold text-sky-800">
                {orders.filter((item) => buildOutboundOrderHeaderSummary(item, item.lines || []).status === 'delivered').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_22px_50px_rgba(15,23,42,0.08)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Nuevo pedido</p>
              <h4 className="mt-1 text-xl font-bold text-slate-950">
                {editingOrderId ? 'Editar pedido guardado' : 'Destino, productos y salida'}
              </h4>
              <p className="mt-2 text-sm text-slate-600">
                {editingOrderId
                  ? 'Ajustá cantidades, precios o destino. Si todavía no entró a logística, lo podés actualizar desde acá.'
                  : 'Prepará el pedido desde stock y dejalo listo para que aparezca en la hoja de ruta como pendiente de entrega.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {editingOrderId ? (
                <button type="button" onClick={onCancelEdit} className={stockButtonTones.ghost}>
                  <Icon path="M6 18 18 6M6 6l12 12" className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
              <button type="button" onClick={addLine} className={stockButtonTones.ghost}>
                <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                Línea
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nombre del pedido</span>
              <input
                type="text"
                value={form.orderLabel}
                onChange={(e) => setForm((prev) => ({ ...prev, orderLabel: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                placeholder="Ej. Pedido oficina Martínez"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Motivo de salida</span>
              <select
                value={form.operationType}
                onChange={(e) => setForm((prev) => ({ ...prev, operationType: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              >
                {OPERATION_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Contacto destino</span>
              <select
                value={form.selectedContactId}
                onChange={(e) => onDestinationSelect(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              >
                <option value="">Cargar destino manualmente</option>
                {contactOptions.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name} {contact.type ? `· ${contact.type}` : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nombre del destino</span>
              <input
                type="text"
                value={form.destinationName}
                onChange={(e) => setForm((prev) => ({ ...prev, selectedContactId: '', destinationName: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                placeholder="Cliente, oficina, instalación o destino"
              />
            </label>

            <label ref={addressAutocompleteRef} className="block md:col-span-2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Dirección operativa</span>
              <div className="relative">
                <input
                  type="text"
                  value={form.destinationAddress}
                  onChange={(e) =>
                    onDestinationAddressPatch({
                      destinationAddress: e.target.value,
                      destinationLatlng: null,
                    })
                  }
                  onFocus={() => {
                    if (addressSuggestions.length > 0) setShowAddressSuggestions(true);
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                  placeholder="Calle y número"
                />
                {loadingAddressSuggestions ? (
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4 animate-spin" />
                  </div>
                ) : null}
                {!selectedContact && showAddressSuggestions && addressSuggestions.length > 0 ? (
                  <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.properties?.place_id || suggestion.properties?.formatted}
                        type="button"
                        onClick={() => handleSelectAddressSuggestion(suggestion)}
                        className="w-full rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                      >
                        <p className="text-sm font-semibold text-slate-900">
                          {suggestion.properties?.formatted ||
                            suggestion.properties?.address_line1 ||
                            'Dirección sugerida'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {[
                            suggestion.properties?.city ||
                              suggestion.properties?.town ||
                              suggestion.properties?.village,
                            suggestion.properties?.state,
                            suggestion.properties?.postcode,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Localidad</span>
              <input
                type="text"
                value={form.destinationLocality}
                onChange={(e) => onDestinationAddressPatch({ destinationLocality: e.target.value, destinationLatlng: null })}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Partido</span>
              <input
                type="text"
                value={form.destinationPartido}
                onChange={(e) => onDestinationAddressPatch({ destinationPartido: e.target.value, destinationLatlng: null })}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Provincia</span>
              <select
                value={form.destinationProvince}
                onChange={(e) => onDestinationAddressPatch({ destinationProvince: e.target.value, destinationLatlng: null })}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              >
                {ARGENTINA_PROVINCES.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Código postal</span>
              <input
                type="text"
                value={form.destinationPostalCode}
                onChange={(e) => onDestinationAddressPatch({ destinationPostalCode: e.target.value, destinationLatlng: null })}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Estado operativo</span>
              <select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              >
                {ORDER_READY_STATES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Referencia comercial</span>
              <input
                type="text"
                value={form.reference}
                onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Remito, orden, presupuesto o referencia interna"
            />
            </label>

            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Validación de ubicación
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{addressValidation.text}</p>
                  {addressValidation.validatedAddress ? (
                    <p className="mt-1 text-xs text-emerald-700">
                      Dirección aprobada: {addressValidation.validatedAddress}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={handleValidateDestinationAddress}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-white px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50"
                >
                  <Icon path="M12 2.25c-4.97 0-9 3.806-9 8.5 0 6.396 8.196 10.517 8.545 10.69a1 1 0 00.91 0C12.804 21.267 21 17.146 21 10.75c0-4.694-4.03-8.5-9-8.5z" className="h-4 w-4" />
                  Validar ubicación en mapa
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                    form.destinationLatlng
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  {form.destinationLatlng
                    ? `Lat ${Number(form.destinationLatlng.lat || 0).toFixed(5)} · Lng ${Number(
                        form.destinationLatlng.lng || 0
                      ).toFixed(5)}`
                    : 'Todavía sin coordenadas validadas'}
                </span>
                {!GEOAPIFY_API_KEY ? (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-700">
                    Sin clave de sugerencias automáticas
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-[24px] border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Productos del pedido</p>
                <p className="mt-1 text-sm text-slate-600">Definí cuánto sale, a cuánto y con qué margen querés preparar esta entrega.</p>
              </div>
              <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {totals.quantity} unidad{totals.quantity !== 1 ? 'es' : ''}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {linesWithProducts.map((line, index) => (
                <div key={line.id} className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Producto</span>
                      <select
                        value={line.productId}
                        onChange={(e) => updateLine(line.id, { productId: e.target.value, quantity: '', saleUnitPrice: '' })}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                      >
                        <option value="">Seleccionar producto</option>
                        {productOptions.map((product) => (
                          <option key={product.id} value={product.id}>
                            {buildProductOptionLabel(product)}
                          </option>
                        ))}
                      </select>
                      {line.product ? (
                        <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                          <div className="grid gap-2 md:grid-cols-2">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                Producto principal
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-900">
                                {getProductPrimaryLabel(line.product)}
                              </p>
                              <p className="mt-1 text-[11px] text-slate-500">
                                {getProductIdentityParts(line.product).join(' · ') || 'Sin modelo, marca ni característica.'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                Stock listo para salida
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-900">
                                {line.availableQty} unidad{line.availableQty !== 1 ? 'es' : ''}
                              </p>
                              <p className="mt-1 text-[11px] text-slate-500">
                                {line.product?.sku ? `SKU ${line.product.sku}` : 'Sin SKU cargado.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </label>

                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(200px,0.92fr)_minmax(170px,0.8fr)_minmax(170px,0.8fr)]">
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Cantidad a preparar</span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={line.quantity}
                          max={line.availableQty || undefined}
                          onChange={(e) => {
                            const nextValue = Math.max(0, Number(e.target.value || 0));
                            const nextQuantity = line.availableQty > 0
                              ? Math.min(nextValue, line.availableQty)
                              : nextValue;
                            updateLine(line.id, { quantity: nextQuantity ? String(nextQuantity) : '' });
                          }}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                          disabled={!line.product || line.availableQty <= 0}
                        />
                        <p className="mt-2 text-[11px] text-slate-500">
                          {line.product
                            ? line.availableQty > 0
                              ? `Disponible para preparar: ${line.availableQty}`
                              : 'Sin stock listo para esta salida.'
                            : 'Elegí un producto para ver cuántas unidades podés preparar.'}
                        </p>
                      </label>

                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Venta u.</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.saleUnitPrice === 0 ? '' : line.saleUnitPrice}
                          onChange={(e) => updateLine(line.id, { saleUnitPrice: e.target.value })}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                          placeholder="Manual"
                        />
                      </label>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Venta total</p>
                        <p className="mt-1 font-semibold text-emerald-800">{formatMoney(line.totalSale)}</p>
                        <p className="mt-1 text-[11px] text-emerald-700/80">
                          Margen {formatMoney(line.margin)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-500">Costo origen u.</p>
                          <p className="mt-1 font-semibold text-rose-700">{formatMoney(line.unitCost)}</p>
                          <p className="mt-1 text-[11px] text-rose-600/80">Compra {formatMoney(line.totalCost)}</p>
                        </div>
                        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600">Venta unitaria</p>
                          <p className="mt-1 font-semibold text-cyan-800">{formatMoney(line.saleUnitPrice)}</p>
                          <p className="mt-1 text-[11px] text-cyan-700/80">
                            {line.quantity || 0} u. × {formatMoney(line.saleUnitPrice)}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Resultado</p>
                          <p className={`mt-1 font-semibold ${line.margin >= 0 ? 'text-emerald-800' : 'text-rose-600'}`}>
                            {formatMoney(line.margin)}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-500">
                            Compra {formatMoney(line.totalCost)} · Venta {formatMoney(line.totalSale)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-end justify-end">
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          disabled={linesWithProducts.length === 1}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Eliminar línea ${index + 1}`}
                        >
                          <Icon path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166" className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Compra total</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{formatMoney(totals.totalCost)}</p>
              </div>
              <div className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600">Venta total</p>
                <p className="mt-1 text-lg font-bold text-cyan-800">{formatMoney(totals.totalSale)}</p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Margen</p>
                <p className={`mt-1 text-lg font-bold ${totals.margin >= 0 ? 'text-emerald-800' : 'text-rose-600'}`}>
                  {formatMoney(totals.margin)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={onGeocodeDestination} className={stockButtonTones.ghost}>
                <Icon path="M12 2.25c-3.728 0-6.75 3.022-6.75 6.75 0 5.477 6.75 12.75 6.75 12.75S18.75 14.477 18.75 9c0-3.728-3.022-6.75-6.75-6.75z" className="h-4 w-4" />
                Validar destino
              </button>
              <button type="button" onClick={onCreate} className={stockButtonTones.primary}>
                <Icon path="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" />
                {editingOrderId ? 'Actualizar pedido' : 'Guardar pedido'}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_22px_50px_rgba(15,23,42,0.08)]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Entrega, garantía y retiro</p>
          <h4 className="mt-1 text-xl font-bold text-slate-950">Pedidos activos y seguimiento</h4>
          <p className="mt-2 text-sm text-slate-600">
            Acá vive el seguimiento real del pedido: cuando queda listo para logística, cuando entra en garantía y cuando queda aprobado para retiro.
          </p>

          <div className="mt-4 space-y-3">
            {activeOrders.length === 0 ? (
              <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No hay pedidos activos en esta instancia. Los entregados quedan reflejados en el ciclo del producto.
              </div>
            ) : (
              activeOrders.map((order) => {
                const summary = buildOutboundOrderHeaderSummary(order, order.lines || []);
                const displayStatus = getDisplayOrderStatus(order);
                const destinationText = order.destinationAddressSnapshot || formatAddress(order.destinationSnapshot || {});
                return (
                  <div key={order.id} className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-bold text-slate-900">
                            {order.orderNumber || order.id}
                          </p>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${OUTBOUND_STATUS_STYLES[displayStatus] || OUTBOUND_STATUS_STYLES.draft}`}>
                            {OUTBOUND_STATUS_LABELS[displayStatus] || OUTBOUND_STATUS_LABELS.draft}
                          </span>
                          <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
                            {OPERATION_TYPE_OPTIONS.find((item) => item.value === order.operationType)?.label || 'Salida'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm font-medium text-slate-700">
                          {order.contactNameSnapshot || order.destinationNameSnapshot || 'Destino sin nombre'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">{destinationText || 'Dirección todavía no validada.'}</p>
                        {displayStatus === 'in_warranty' ? (
                          <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-[11px] font-semibold text-fuchsia-700">
                            <span className="inline-flex h-2 w-2 rounded-full bg-fuchsia-500 animate-pulse" />
                            En garantía{order.warrantyEnteredAt ? ` · ${formatDaysSince(order.warrantyEnteredAt)}` : ''}
                          </p>
                        ) : null}
                        {displayStatus === 'ready_for_pickup' ? (
                          <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[11px] font-semibold text-indigo-700">
                            <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500" />
                            Retiro aprobado
                            {order.warrantyOutcome ? ` · ${WARRANTY_OUTCOME_LABELS[order.warrantyOutcome] || order.warrantyOutcome}` : ''}
                          </p>
                        ) : null}
                        {order.operationType === 'warranty' ? (
                          <p className="mt-2 text-xs font-medium text-slate-500">
                            Ciclo garantía: salida a service, permanencia en garantía y habilitación posterior de retiro.
                          </p>
                        ) : null}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>{summary.totals.quantity} unidad{summary.totals.quantity !== 1 ? 'es' : ''}</p>
                        <p>Compra {formatMoney(order.totalCost)}</p>
                        <p>Venta {formatMoney(order.totalSale)}</p>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2">
                      {(order.lines || []).map((line) => (
                        <div key={line.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white bg-white px-3 py-2 text-sm">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">{line.productNameSnapshot || 'Producto'}</p>
                            <p className="text-xs text-slate-500">
                              {line.quantity} u. · Venta {formatMoney(line.saleUnitPrice)} · Margen {formatMoney((toSafeNumber(line.saleUnitPrice) - toSafeNumber(line.unitCost)) * toSafeNumber(line.quantity))}
                            </p>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${OUTBOUND_STATUS_STYLES[computeOutboundOrderLineStatus(line)] || OUTBOUND_STATUS_STYLES.draft}`}>
                            {LINE_STATUS_LABELS[computeOutboundOrderLineStatus(line)] || 'Estado'}
                          </span>
                        </div>
                      ))}
                    </div>

                    {order.operationType === 'warranty' && displayStatus === 'in_warranty' ? (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-fuchsia-200 bg-fuchsia-50 px-3 py-3">
                        <div className="text-sm text-fuchsia-800">
                          <p className="font-semibold">Esperando respuesta del service</p>
                          <p className="mt-1 text-xs text-fuchsia-700">
                            Cuando la garantía confirme el resultado, aprobás el retiro y vuelve a logística como próximo paso operativo.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => onApproveWarrantyPickup(order.id, 'fixed')}
                            className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                          >
                            Arreglada
                          </button>
                          <button
                            type="button"
                            onClick={() => onApproveWarrantyPickup(order.id, 'not_repaired')}
                            className="inline-flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100"
                          >
                            No reparada
                          </button>
                          <button
                            type="button"
                            onClick={() => onApproveWarrantyPickup(order.id, 'replaced_new')}
                            className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100"
                          >
                            Cambio por nuevo
                          </button>
                        </div>
                      </div>
                    ) : ['packing', 'ready_for_delivery', 'cancelled'].includes(displayStatus) ? (
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        {displayStatus !== 'cancelled' ? (
                          <button type="button" onClick={() => onEditOrder(order.id)} className={stockButtonTones.ghost}>
                            <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" className="h-4 w-4" />
                            Editar
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => onDeleteOrder(order.id)}
                          className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          <Icon path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166" className="h-4 w-4" />
                          {displayStatus === 'ready_for_delivery' ? 'Cancelar' : 'Eliminar'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockOutboundOrdersPanel;
